/**
 * Black Box — pick mode. Lets the dashboard's Flow Setup page ask a page (loaded
 * in an iframe, same- or cross-origin — postMessage works for both, so there's no
 * separate same-origin code path) to report back the selector for the next
 * element a human clicks. Reuses elId()/clickTarget() verbatim so a picked
 * selector always matches what this same tracer would independently detect for
 * that element at runtime.
 *
 * Inert unless armed: a page that never receives a `bb-pick-start` message pays
 * for one `message` listener and nothing else — safe to ship to every page,
 * including live production traffic.
 */
import { elId, clickTarget, isRendered } from './util.js';
import { snapshotDom } from './dom.js';

const OUTLINE = '2px solid #38bdf8';

let armed = false;
let hoverEl = null;
let hoverPrevOutline = '';

function bestLabel(el) {
  try {
    const t = (el.textContent || '').trim().replace(/\s+/g, ' ');
    if (t) return t.slice(0, 60);
    const alt = el.getAttribute && (el.getAttribute('placeholder') || el.getAttribute('aria-label') || el.getAttribute('alt'));
    if (alt) return alt.slice(0, 60);
  } catch (_) {}
  return null;
}

function setHover(el) {
  if (hoverEl === el) return;
  try { if (hoverEl && hoverEl.style) hoverEl.style.outline = hoverPrevOutline; } catch (_) {}
  hoverEl = el;
  try {
    if (el && el.style) { hoverPrevOutline = el.style.outline || ''; el.style.outline = OUTLINE; }
  } catch (_) {}
}

function clearHover() { setHover(null); }

function onMouseOver(e) { try { setHover(clickTarget(e.target)); } catch (_) {} }

// ---- highlight: point at a KNOWN selector on demand (e.g. "Most clicked" row
// clicked in the Heatmap dashboard) — distinct from hover/pick, which are about
// discovering a NEW selector. Persists until cleared or a different one is requested.
//
// Drawn as our own overlay (a spotlight that dims the rest of the page + a pulsing
// ring + a label) instead of styling the element: an outline on the element itself
// gets clipped by overflow:hidden parents and is easy to miss on busy pages.
let highlightEl = null;
let overlay = null;
let trackRaf = 0;
const STYLE_ID = 'bb-hl-style';

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID;
  st.textContent =
    '.bb-hl{position:fixed;z-index:2147483646;pointer-events:none;border-radius:8px;' +
    'border:3px solid #f43f5e;box-shadow:0 0 0 9999px rgba(9,9,11,.55),0 0 24px 6px rgba(244,63,94,.6);' +
    'transition:top .15s,left .15s,width .15s,height .15s;animation:bbhl 1s ease-out 3}' +
    '.bb-hl-tag{position:absolute;left:-3px;bottom:100%;margin-bottom:6px;white-space:nowrap;' +
    'max-width:320px;overflow:hidden;text-overflow:ellipsis;' +
    'background:#f43f5e;color:#fff;font:600 12px/1.2 ui-sans-serif,system-ui,sans-serif;' +
    'padding:4px 8px;border-radius:6px;box-shadow:0 4px 12px rgba(0,0,0,.35)}' +
    '.bb-hl-tag.below{bottom:auto;top:100%;margin:6px 0 0}' +
    '@keyframes bbhl{0%{box-shadow:0 0 0 9999px rgba(9,9,11,.55),0 0 0 0 rgba(244,63,94,.9)}' +
    '100%{box-shadow:0 0 0 9999px rgba(9,9,11,.55),0 0 0 18px rgba(244,63,94,0)}}';
  (document.head || document.documentElement).appendChild(st);
}

// Keep the overlay glued to the element while it's shown (the page can scroll,
// re-layout, or animate the element) — one rect read per frame, highlight only.
function track() {
  trackRaf = 0;
  if (!highlightEl || !overlay) return;
  try {
    const r = highlightEl.getBoundingClientRect();
    const pad = 4;
    overlay.style.top = (r.top - pad) + 'px';
    overlay.style.left = (r.left - pad) + 'px';
    overlay.style.width = (r.width + pad * 2) + 'px';
    overlay.style.height = (r.height + pad * 2) + 'px';
    const tag = overlay.firstChild;
    if (tag) tag.className = 'bb-hl-tag' + (r.top < 36 ? ' below' : '');
  } catch (_) {}
  trackRaf = window.requestAnimationFrame ? window.requestAnimationFrame(track) : 0;
}

function clearHighlight() {
  if (trackRaf && window.cancelAnimationFrame) window.cancelAnimationFrame(trackRaf);
  trackRaf = 0;
  try { if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay); } catch (_) {}
  overlay = null;
  highlightEl = null;
}

// The element a selector means on the CURRENT screen: the first match that's
// rendered AND on screen. A step that isn't showing, a duplicate CTA in a hidden
// section, or a closed bottom sheet parked off-screen with translateY(110%) would
// otherwise win — the dashboard then scrolled to empty space under the page.
// (The preview frame is as tall as the page, so in-flow content is always inside
// the viewport; only off-screen fixed layers fail this.) Null when none is visible.
function onScreen(el) {
  try {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
  } catch (_) { return false; }
}
function visibleMatch(selector) {
  let all = [];
  try { all = document.querySelectorAll(selector); } catch (_) { return null; }
  for (let i = 0; i < all.length; i++) if (isRendered(all[i]) && onScreen(all[i])) return all[i];
  return null;
}

// Returns the element's rect (iframe-viewport-relative — the dashboard renders
// pages at a fixed unscaled width, so no coordinate translation is needed) or
// null if nothing visible matches the selector on the current page.
function highlight(selector, label) {
  clearHighlight();
  const el = visibleMatch(selector);
  if (!el) return null;
  highlightEl = el;
  try {
    ensureStyle();
    overlay = document.createElement('div');
    overlay.className = 'bb-hl';
    const tag = document.createElement('div');
    tag.className = 'bb-hl-tag';
    tag.textContent = label || selector;
    overlay.appendChild(tag);
    document.body.appendChild(overlay);
    track();
    const r = el.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  } catch (_) { return null; }
}

// Bottom of the page's real content, so the dashboard can size its preview frame
// to the page instead of a fixed guess (cross-origin, it can't measure us itself).
// scrollHeight and wrapper boxes don't work: pages wrap everything in a
// min-height:100vh box, which tracks the FRAME's height and so never shrinks.
// Leaf elements carry the real content; ones inside a fixed layer (modal, sticky
// bar) are skipped — their offsetParent chain stops at the fixed element, not body.
const MAX_SCAN = 6000;
function contentHeight() {
  let bottom = 0;
  try {
    const body = document.body;
    if (!body) return 0;
    const sy = window.scrollY || 0;
    const all = body.getElementsByTagName('*');
    for (let i = 0; i < all.length && i < MAX_SCAN; i++) {
      const el = all[i];
      if (el.children.length) continue;
      let p = el.offsetParent;
      if (!p) continue; // hidden, or fixed itself
      while (p && p !== body) p = p.offsetParent;
      if (p !== body) continue; // inside a fixed layer
      const b = el.getBoundingClientRect().bottom + sy;
      if (b > bottom) bottom = b;
    }
  } catch (_) {}
  return Math.ceil(bottom);
}

// Report the content height to the dashboard on load and whenever it changes
// (step switches hide/show whole sections). Only once the dashboard has spoken
// to us (it sent a message), so ordinary visitors never run this.
let sizeTimer = 0, lastSize = 0;
function reportSize() {
  const h = contentHeight();
  if (!h || h === lastSize) return;
  lastSize = h;
  try { window.parent.postMessage({ type: 'bb-page-size', height: h }, '*'); } catch (_) {}
}
function watchSize() {
  if (sizeTimer) return;
  reportSize();
  sizeTimer = setInterval(reportSize, 500);
}

// If the target belongs to a step other than the one currently showing, jump
// there first — same convention replay.js already relies on (window.Steps.goTo
// by index, matching config.steps' declared order). Pages without that global
// (React templates, sites with no Flow Setup steps yet) just stay put; the
// highlight still runs on whatever's currently rendered. Resolves true/false —
// the caller must know whether the switch actually happened, since showing a
// step's data while a DIFFERENT step is still on screen is worse than not
// switching at all.
function goToStepIfKnown(stepName, configPromise) {
  if (!stepName || !configPromise) return Promise.resolve(false);
  return configPromise.then(function (config) {
    try {
      var keys = Object.keys((config && config.steps) || {});
      var idx = keys.indexOf(stepName);
      if (idx >= 0 && window.Steps && typeof window.Steps.goTo === 'function') {
        window.Steps.goTo(idx);
        return true;
      }
    } catch (_) {}
    return false;
  }, function () { return false; });
}

function onClick(e) {
  let selector = 'unknown', label = null;
  try {
    e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
    const el = clickTarget(e.target);
    selector = elId(el);
    label = bestLabel(el);
  } catch (_) {}
  disarm();
  try { window.parent.postMessage({ type: 'bb-pick-result', selector, label }, '*'); } catch (_) {}
}

function arm() {
  if (armed) return;
  armed = true;
  try {
    document.addEventListener('mouseover', onMouseOver, true);
    document.addEventListener('click', onClick, true);
  } catch (_) {}
}

function disarm() {
  if (!armed) return;
  armed = false;
  try {
    document.removeEventListener('mouseover', onMouseOver, true);
    document.removeEventListener('click', onClick, true);
  } catch (_) {}
  clearHover();
}

export function installPicker(configPromise) {
  if (typeof window === 'undefined') return;
  try {
    window.addEventListener('message', function (e) {
      const d = e && e.data;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'bb-size-request') watchSize();
      else if (d.type === 'bb-pick-start') arm();
      else if (d.type === 'bb-pick-cancel') disarm();
      else if (d.type === 'bb-snapshot-request') {
        // One-shot: "AI Generate" wants this page's current DOM map, on demand —
        // same structure-only capture the tracer already sends once per session
        // (dom.js), just triggered by the dashboard instead of page load.
        let dom = [];
        try { dom = snapshotDom(null); } catch (_) {}
        try { window.parent.postMessage({ type: 'bb-snapshot-result', dom }, '*'); } catch (_) {}
      } else if (d.type === 'bb-highlight-request') {
        goToStepIfKnown(d.step, configPromise).then(function (stepChanged) {
          // A step switch is a synchronous class/style toggle on our own static
          // pages, but give the browser one paint before measuring — a plain
          // rAF is enough and doesn't assume anything about the page's own code.
          var measure = function () {
            const rect = highlight(d.selector, d.label);
            try { window.parent.postMessage({ type: 'bb-highlight-result', selector: d.selector, step: d.step, found: !!rect, rect, stepChanged: stepChanged }, '*'); } catch (_) {}
          };
          if (stepChanged && window.requestAnimationFrame) window.requestAnimationFrame(function () { window.requestAnimationFrame(measure); });
          else measure();
        });
      } else if (d.type === 'bb-highlight-clear') {
        clearHighlight();
      } else if (d.type === 'bb-step-request') {
        // Same navigation as a highlight, without a specific element — the
        // Heatmap's step filter (Phone/Otp/…) uses this to actually move the
        // live page, not just filter which recorded dots are shown.
        goToStepIfKnown(d.step, configPromise).then(function (stepChanged) {
          try { window.parent.postMessage({ type: 'bb-step-result', step: d.step, stepChanged: stepChanged }, '*'); } catch (_) {}
        });
      }
    });
  } catch (_) {}
}
